import React from 'react';
import { useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { skipReasonLabel, type IrrigationEvent } from '../../domain/Irrigation';
import { useIrrigationHistory } from '../../application/use-cases/irrigation/useIrrigationHistory';
import IrrigationHistoryChart from '../components/IrrigationHistoryChart';
import MeasurementChart, { type ChartEvent } from '../components/MeasurementChart';
import './IrrigationHistory.css';

function hasStarted(event: IrrigationEvent): event is IrrigationEvent & ChartEvent {
  return event.startedAt !== null;
}

function formatDuration(startIso: string, endIso: string | null): string {
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const minutes = Math.max(1, Math.round((end - start) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

const IrrigationHistory: React.FC = () => {
  const { id, channel: channelParam } = useParams<{ id: string; channel: string }>();
  const valve = React.useMemo(() => ({ deviceId: id, channel: Number(channelParam) || 1 }), [id, channelParam]);
  const { device, valveName, events, dailyLiters, probe, humidityMeasurements, loading, error } = useIrrigationHistory(valve);

  // Solo los riegos que realmente abrieron la válvula tienen un rango de
  // tiempo real (startedAt no-null) — los salteados quedan afuera de la
  // superposición sobre el gráfico de humedad.
  const wateredEvents = events.filter(hasStarted);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/app/devices/${id}`} />
          </IonButtons>
          <IonTitle>Historial de riego</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}

        {!loading && error && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {!loading && !error && (
          <>
            <IonText color="medium">
              <p style={{ fontSize: 13, marginTop: 0 }}>
                {valveName} · {device?.name}
              </p>
            </IonText>

            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Litros por día</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IrrigationHistoryChart dailyLiters={dailyLiters} />
              </IonCardContent>
            </IonCard>

            {probe && humidityMeasurements.length > 1 && (
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>
                    Humedad de suelo · {probe.name}
                    {probe.device.id !== id ? ` (${probe.device.name})` : ''}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonText color="medium">
                    <p style={{ fontSize: 12.5, marginTop: 0 }}>Las franjas marcan cuándo regó esta válvula.</p>
                  </IonText>
                  <MeasurementChart
                    measurements={humidityMeasurements}
                    unit="%"
                    events={wateredEvents}
                    eventColor="var(--domiot-accent)"
                  />
                </IonCardContent>
              </IonCard>
            )}

            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Eventos</IonCardTitle>
              </IonCardHeader>
              <IonCardContent className="irrigation-history-list">
                {events.length === 0 && (
                  <IonText color="medium">
                    <p>Todavía no hay riegos registrados en esta válvula.</p>
                  </IonText>
                )}
                {events.map((event) => (
                  <div className="irrigation-history-row" key={event.id}>
                    <div>
                      <p className="irrigation-history-main">
                        {event.status === 'skipped'
                          ? event.skipReason
                            ? skipReasonLabel[event.skipReason]
                            : 'Salteado'
                          : event.triggerSource === 'manual'
                            ? 'Riego manual'
                            : 'Riego programado'}
                      </p>
                      <p className="irrigation-history-meta">
                        {event.startedAt
                          ? new Date(event.startedAt).toLocaleString('es-AR')
                          : new Date().toLocaleDateString('es-AR')}
                        {event.status === 'running' && ' · en curso'}
                      </p>
                    </div>
                    {event.startedAt ? (
                      <span className="irrigation-history-liters">
                        {event.estimatedLiters !== null ? `${event.estimatedLiters} L` : formatDuration(event.startedAt, event.endedAt)}
                      </span>
                    ) : (
                      <span className="irrigation-history-skipped">Salteado</span>
                    )}
                  </div>
                ))}
              </IonCardContent>
            </IonCard>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default IrrigationHistory;
